import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import html2canvas from 'html2canvas';
import lebenslaufContent from './lebenslaufContent';
import { buildCvPdf, loadCircularPhoto } from '../utils/cvPdf';
import './Lebenslauf.css';

const skillBars = [
  { name: 'JavaScript/TypeScript', level: 95 },
  { name: 'HTML5/CSS3', level: 90 },
  { name: 'PHP', level: 88 },
  { name: 'Python', level: 95 },
  { name: 'React/Vue/Angular', level: 90 },
  { name: 'TYPO3/WordPress', level: 95 },
  { name: 'Node.js', level: 85 },
  { name: 'SQL/NoSQL', level: 90 },
  { name: 'Java', level: 80 },
  { name: 'Git', level: 92 },
  { name: 'DevOps', level: 65 },
  { name: 'AWS/Azure', level: 80 },
];

// Ausblendbares CV-Element. Im Bearbeitungsmodus bleibt es (abgedunkelt) mit
// Auge-Button sichtbar, sonst wird es gar nicht erst gerendert.
function Hideable({ id, as: Tag = 'div', className = '', ctx, children }) {
  const isHidden = ctx.hidden.includes(id);
  if (isHidden && !ctx.editing) return null;
  const classes = [className, ctx.editing && 'cv-editable', isHidden && 'cv-hidden']
    .filter(Boolean)
    .join(' ');
  return (
    <Tag className={classes}>
      {ctx.editing && (
        <button
          type="button"
          className="cv-toggle"
          title={isHidden ? ctx.ui.showElement : ctx.ui.hideElement}
          onClick={() => ctx.toggle(id)}
        >
          {isHidden ? '🚫' : '👁'}
        </button>
      )}
      {children}
    </Tag>
  );
}

function Lebenslauf() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentAddress, setCurrentAddress] = useState('berlin');
  const [cvLanguage, setCvLanguage] = useState(() => {
    return localStorage.getItem('lebenslauf-language') || 'de';
  });
  const [showPhoto, setShowPhoto] = useState(() => {
    const stored = localStorage.getItem('lebenslauf-show-photo');
    return stored === null ? true : stored === 'true';
  });
  const [editing, setEditing] = useState(false);
  const [hidden, setHidden] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('lebenslauf-hidden'));
      return Array.isArray(stored) ? stored : [];
    } catch (e) {
      return [];
    }
  });
  const [downloadingImage, setDownloadingImage] = useState(false);
  const cvRef = useRef(null);

  const c = lebenslaufContent[cvLanguage];
  const ui = c.ui;

  const switchCvLanguage = (lang) => {
    setCvLanguage(lang);
    localStorage.setItem('lebenslauf-language', lang);
  };

  const togglePhoto = () => {
    setShowPhoto((prev) => {
      const next = !prev;
      localStorage.setItem('lebenslauf-show-photo', String(next));
      return next;
    });
  };

  const updateHidden = (next) => {
    setHidden(next);
    localStorage.setItem('lebenslauf-hidden', JSON.stringify(next));
  };

  const toggleElement = (id) => {
    updateHidden(hidden.includes(id) ? hidden.filter((h) => h !== id) : [...hidden, id]);
  };

  const ctx = { hidden, editing, toggle: toggleElement, ui };

  // Ausgeblendete Elemente und Bearbeitungs-Buttons kommen nie in PDF/Bild.
  const captureOptions = {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    width: 1200,
    ignoreElements: (el) =>
      el.classList &&
      (el.classList.contains('cv-hidden') || el.classList.contains('cv-toggle')),
    onclone: (doc) => {
      const cv = doc.querySelector('.cv-container');
      if (cv) cv.classList.remove('cv-editing');
    },
  };

  const addresses = {
    berlin: {
      display: 'Gartenfelderstraße 74b, 13599 Berlin',
    },
    nuremberg: {
      display: 'Westendstraße 28, 90427 Nürnberg',
    },
  };

  const fileSuffix = () => {
    const place = currentAddress === 'berlin' ? 'Berlin' : 'Nuremberg';
    return cvLanguage === 'en' ? `${place}_EN` : place;
  };

  // Vektor-PDF im Layout der Webansicht mit echtem Text (kein Screenshot):
  // maschinenlesbar für ATS, berücksichtigt ausgeblendete Elemente und Foto-Schalter.
  const downloadPDF = async () => {
    try {
      const photo = showPhoto ? await loadCircularPhoto().catch(() => null) : null;
      buildCvPdf({
        c,
        skillBars,
        address: addresses[currentAddress].display,
        hidden,
        photo,
      }).save(`Emin_Tavli_Lebenslauf_${fileSuffix()}.pdf`);
    } catch (error) {
      console.error('PDF error:', error);
      alert(ui.errorPdf);
    }
  };

  const downloadImage = async () => {
    setDownloadingImage(true);
    try {
      const canvas = await html2canvas(cvRef.current, captureOptions);

      const link = document.createElement('a');
      link.download = `Emin_Tavli_Lebenslauf_${fileSuffix()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Image error:', error);
      alert(ui.errorImage);
    } finally {
      setDownloadingImage(false);
    }
  };

  return (
    <div className="lebenslauf-page">
      <button
        type="button"
        className="back-button"
        onClick={() => {
          const scrollTo = location.state && location.state.scrollTo ? location.state.scrollTo : 'contact';
          navigate('/', { state: { scrollTo } });
        }}
      >
        {ui.back}
      </button>

      <div className="lebenslauf-controls">
        <div className="address-selector">
          <button
            className={`address-btn ${currentAddress === 'berlin' ? 'active' : ''}`}
            onClick={() => setCurrentAddress('berlin')}
          >
            {ui.addressBerlin}
          </button>
          <button
            className={`address-btn ${currentAddress === 'nuremberg' ? 'active' : ''}`}
            onClick={() => setCurrentAddress('nuremberg')}
          >
            {ui.addressNuremberg}
          </button>
        </div>

        <div className="address-selector">
          <button
            className={`address-btn ${cvLanguage === 'de' ? 'active' : ''}`}
            onClick={() => switchCvLanguage('de')}
          >
            Deutsch
          </button>
          <button
            className={`address-btn ${cvLanguage === 'en' ? 'active' : ''}`}
            onClick={() => switchCvLanguage('en')}
          >
            English
          </button>
        </div>

        <div className="photo-toggle">
          <button
            className={`address-btn ${showPhoto ? 'active' : ''}`}
            onClick={togglePhoto}
          >
            {showPhoto ? ui.hidePhoto : ui.showPhoto}
          </button>
        </div>

        <div className="photo-toggle">
          <button
            className={`address-btn ${editing ? 'active' : ''}`}
            onClick={() => setEditing((prev) => !prev)}
          >
            {editing ? ui.editDone : ui.editStart}
          </button>
          {hidden.length > 0 && (
            <button className="address-btn" onClick={() => updateHidden([])}>
              {ui.showAll}
            </button>
          )}
        </div>

        <div className="download-buttons">
          <button
            className="download-btn"
            onClick={downloadPDF}
          >
            {ui.downloadPdf}
          </button>
          <button
            className="download-btn"
            onClick={downloadImage}
            disabled={downloadingImage}
          >
            {downloadingImage ? ui.downloadingImage : ui.downloadImage}
          </button>
        </div>
      </div>

      <div className={`cv-container${editing ? ' cv-editing' : ''}`} ref={cvRef}>
        <div className="cv-left">
          <div className="profile-section">
            {showPhoto && (
              <img
                src="/img/portrait.png"
                alt={c.name}
                className="profile-photo"
              />
            )}
            <div className="name">{c.name}</div>
            <div className="title">{c.title}</div>
          </div>

          <Hideable id="summary" className="summary" ctx={ctx}>
            {c.summary}
          </Hideable>

          <Hideable id="contact" className="cv-section" ctx={ctx}>
            <div className="cv-section-title">{c.sections.contact}</div>
            <div className="contact-info">
              <Hideable id="contact-address" as="p" ctx={ctx}>{addresses[currentAddress].display}</Hideable>
              <Hideable id="contact-phone" as="p" ctx={ctx}>01727049258</Hideable>
              <Hideable id="contact-email" as="p" ctx={ctx}>Tavlin@hotmail.de</Hideable>
              <Hideable id="contact-birth" as="p" ctx={ctx}>{c.birthDate}</Hideable>
            </div>
          </Hideable>

          <Hideable id="skills" className="cv-section" ctx={ctx}>
            <div className="cv-section-title">{c.sections.skills}</div>
            <ul className="skills-list">
              {skillBars.map((skill, idx) => (
                <Hideable id={`skill-${idx}`} as="li" className="skill-item" key={idx} ctx={ctx}>
                  <div className="skill-name">{skill.name}</div>
                  <div className="skill-bar">
                    <div
                      className="skill-progress"
                      style={{ width: `${skill.level}%` }}
                    ></div>
                  </div>
                </Hideable>
              ))}
            </ul>
          </Hideable>

          <Hideable id="tech" className="cv-section" ctx={ctx}>
            <div className="cv-section-title">{c.sections.tech}</div>
            <p className="tech-list">{c.tech}</p>
          </Hideable>

          <Hideable id="languages" className="cv-section" ctx={ctx}>
            <div className="cv-section-title">{c.sections.languages}</div>
            <div className="languages">
              {c.languages.map((lang, idx) => (
                <Hideable id={`lang-${idx}`} className="language-item" key={idx} ctx={ctx}>
                  <span>{lang.name}</span>
                  <span>{lang.level}</span>
                </Hideable>
              ))}
            </div>
          </Hideable>
        </div>

        <div className="cv-right">
          <Hideable id="experience" ctx={ctx}>
            <h2 className="main-title">{c.sections.experience}</h2>

            {c.experience.map((job, idx) => (
              <Hideable id={`exp-${idx}`} className="experience-item" key={idx} ctx={ctx}>
                <div className="job-title">{job.role}</div>
                <div className="company">{job.company}</div>
                <div className="duration">{job.duration}</div>
                <ul className="responsibilities">
                  {job.bullets.map((bullet, bIdx) => (
                    <Hideable id={`exp-${idx}-b${bIdx}`} as="li" key={bIdx} ctx={ctx}>{bullet}</Hideable>
                  ))}
                </ul>
              </Hideable>
            ))}
          </Hideable>

          <Hideable id="education" ctx={ctx}>
            <h3 className="section-header">{c.sections.education}</h3>

            {c.education.map((edu, idx) => (
              <Hideable id={`edu-${idx}`} className="education-item" key={idx} ctx={ctx}>
                <div className="degree-title">{edu.degree}</div>
                <div className="institution">{edu.institution}</div>
                <div className="duration">{edu.duration}</div>
                <ul className="details">
                  {edu.bullets.map((bullet, bIdx) => (
                    <Hideable id={`edu-${idx}-b${bIdx}`} as="li" key={bIdx} ctx={ctx}>{bullet}</Hideable>
                  ))}
                </ul>
              </Hideable>
            ))}
          </Hideable>

          <Hideable id="additional" className="additional-info" ctx={ctx}>
            <h3 className="section-header">{c.sections.additional}</h3>

            <Hideable id="additional-school" className="additional-section" ctx={ctx}>
              <h4>{c.sections.schoolEducation}</h4>
              {c.additional.schoolEducation.map((line, idx) => (
                <p key={idx}>{line}</p>
              ))}
            </Hideable>

            <Hideable id="additional-early" className="additional-section" ctx={ctx}>
              <h4>{c.sections.earlyExperience}</h4>
              {c.additional.earlyExperience.map((line, idx) => (
                <p key={idx}>{line}</p>
              ))}
            </Hideable>
          </Hideable>
        </div>
      </div>
    </div>
  );
}

export default Lebenslauf;
